import { useEffect, useRef, useState } from "react";
import { RecordingPlayer } from "../audio/RecordingPlayer";
import { resolveMediaUrl } from "../../lib/media-url";
import type { TurnScore } from "@a2/core";
import {
	CRITERIA,
	type Criterion,
	type CriterionKey,
} from "../../lib/duo-rubric";
import { cn } from "../../lib/utils";

export type FeedbackBubbleProps = {
	feedbackMd: string;
	score: TurnScore;
	audioUrl: string;
	studentAudioUrl?: string;
	transcript?: string;
	onAudioEnded?: () => void;
};

type Band = "pass" | "neutral" | "warn";

const DETAILS_SUMMARY_CLASSES =
	"cursor-pointer select-none flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge list-none hover:text-[var(--ink-muted)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2 transition-colors";

type Verdict = { band: Band; label: string; tip?: string };

function verdictFor(score: TurnScore): Verdict {
	const total = score.ad + score.wo + score.gr + score.vl + score.helderheid + score.uit;
	if (score.ad < 2 && total >= 8) {
		return {
			band: "warn",
			label: "Onvolledig",
			tip: "Pas op in het examen — een onvolledig antwoord telt als half goed.",
		};
	}
	if (total >= 10) return { band: "pass", label: "Geslaagd" };
	if (total <= 7) return { band: "warn", label: "Bijslijpen" };
	return { band: "neutral", label: "Bijna" };
}

const CRITERION_DUTCH_LABEL: Record<CriterionKey, string> = {
	ad: "Adequaatheid",
	wo: "Woordenschat",
	gr: "Grammatica",
	vl: "Vlotheid",
	helderheid: "Helderheid",
	uit: "Uitspraak",
};

function scoreFor(score: TurnScore, key: CriterionKey): number {
	switch (key) {
		case "ad":
			return score.ad;
		case "wo":
			return score.wo;
		case "gr":
			return score.gr;
		case "vl":
			return score.vl;
		case "helderheid":
			return score.helderheid;
		case "uit":
			return score.uit;
	}
}

function TutorHeader() {
	return (
		<div className="flex items-center gap-3">
			<span
				aria-hidden
				className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[var(--accent)] font-mono-badge text-[13px] font-semibold text-white"
			>
				T
			</span>
			<span className="leading-tight">
				<span className="block text-sm font-semibold text-[var(--ink)]">Tutor</span>
				<span className="block font-mono-badge text-[11px] uppercase tracking-wider text-[var(--ink-faint)]">
					Feedback
				</span>
			</span>
		</div>
	);
}

function VerdictBand({
	band,
	label,
	total,
	max,
	tip,
}: {
	band: Band;
	label: string;
	total: number;
	max: number;
	tip?: string;
}) {
	const chipClass =
		band === "pass"
			? "bg-[var(--pass)]/15 text-[var(--pass)]"
			: band === "warn"
				? "bg-[var(--warn)]/15 text-[var(--warn)]"
				: "bg-[var(--accent-soft)] text-[var(--accent)]";
	return (
		<div className="space-y-1.5">
			<div className="flex items-center gap-3">
				<span
					className={`inline-flex items-center gap-2 rounded-full px-3 py-1 font-mono-badge text-[11px] font-semibold uppercase tracking-wide ${chipClass}`}
				>
					{label}
				</span>
				<span className="ml-auto font-mono-badge text-sm text-[var(--ink-muted)] tabular-nums">
					{total}/{max}
				</span>
			</div>
			{tip && (
				<p className="text-xs text-[var(--ink-muted)] leading-relaxed">{tip}</p>
			)}
		</div>
	);
}

function CriterionBar({
	c,
	value,
	label,
}: {
	c: Criterion;
	value: number;
	label: string;
}) {
	const filled = c.max > 0 ? value / c.max : 0;
	return (
		<div className="flex items-center gap-2 text-xs">
			<span className="w-7 font-mono-badge text-[var(--ink-faint)] uppercase tracking-wider">
				{c.abbr ?? c.key}
			</span>
			<span className="flex-1 text-[var(--ink-muted)]">{label}</span>
			<span
				aria-hidden
				className="w-24 h-1.5 rounded-full bg-[var(--panel-soft)] overflow-hidden"
			>
				<span
					className="block h-full rounded-full"
					style={{
						width: `${filled * 100}%`,
						background:
							filled >= 0.75
								? "var(--pass)"
								: filled >= 0.5
									? "var(--accent)"
									: "var(--warn)",
					}}
				/>
			</span>
			<span className="w-8 text-right font-mono-badge text-[var(--ink-muted)] tabular-nums">
				{value}/{c.max}
			</span>
		</div>
	);
}

export function FeedbackBubble({
	feedbackMd,
	score,
	audioUrl,
	studentAudioUrl,
	transcript,
	onAudioEnded,
}: FeedbackBubbleProps) {
	const ref = useRef<HTMLAudioElement | null>(null);
	const [autoplayBlocked, setAutoplayBlocked] = useState(false);
	const [playing, setPlaying] = useState(false);

	function pauseOtherAudio() {
		if (typeof document === "undefined") return;
		document.querySelectorAll("audio").forEach((a) => {
			if (a !== ref.current && !a.paused) a.pause();
		});
	}

	useEffect(() => {
		setAutoplayBlocked(false);
		setPlaying(false);
		if (!ref.current || !audioUrl) return;
		pauseOtherAudio();
		ref.current
			.play()
			.then(() => setPlaying(true))
			.catch(() => setAutoplayBlocked(true));
	}, [audioUrl]);

	const [audioError, setAudioError] = useState<string | null>(null);
	function toggle() {
		const a = ref.current;
		if (!a) return;
		if (a.paused) {
			pauseOtherAudio();
			setAudioError(null);
			if (a.readyState < 2) a.load();
			a.play()
				.then(() => {
					setPlaying(true);
					setAutoplayBlocked(false);
				})
				.catch((err: unknown) => {
					const msg = err instanceof Error ? err.message : "afspelen mislukt";
					console.warn(`[feedback-audio] play failed: ${msg}`);
					setAudioError("Afspelen lukt niet — probeer opnieuw.");
				});
		} else {
			a.pause();
			setPlaying(false);
		}
	}

	const dutch = feedbackMd.split("\n---")[0]?.trim() ?? feedbackMd;
	const english = feedbackMd.split("\n---")[1]?.trim() ?? "";
	const total = score.ad + score.wo + score.gr + score.vl + score.helderheid + score.uit;
	const verdict = verdictFor(score);
	const max = 12;

	if (total === 0 && (!transcript || transcript.trim() === "")) {
		return (
			<article
				role="region"
				aria-label="Feedback op uw antwoord"
				aria-live="polite"
				className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--accent-soft)] p-5 sm:p-6 space-y-4"
			>
				<TutorHeader />
				<VerdictBand band="warn" label="Bijslijpen" total={0} max={max} />
				<p className="font-serif-italic italic text-[var(--ink-muted)]">
					Geen verstaanbare spraak.
				</p>
				<p className="text-sm text-[var(--ink-faint)]">
					Geen opname — probeer opnieuw met de knop hieronder.
				</p>
			</article>
		);
	}

	return (
		<article
			role="region"
			aria-label="Feedback op uw antwoord"
			aria-live="polite"
			className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--accent-soft)] p-5 sm:p-6 space-y-4"
		>
			<TutorHeader />

			<VerdictBand
				band={verdict.band}
				label={verdict.label}
				total={total}
				max={max}
				tip={verdict.tip}
			/>

			<p className="font-serif-italic text-xl sm:text-2xl leading-relaxed">
				{dutch}
			</p>

			{audioUrl || studentAudioUrl ? (
				<div className="flex flex-wrap items-center gap-3">
					{audioUrl ? (
						<>
							<button
								type="button"
								onClick={toggle}
								className={cn(
									"inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors",
									playing
										? "border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--ink)]"
										: "border-[var(--border)] bg-[var(--panel-soft)] hover:bg-[var(--panel)] text-[var(--ink-muted)]",
								)}
								aria-pressed={playing}
								aria-label={playing ? "Pauze" : "Hoor de feedback"}
							>
								<span className="font-mono-badge text-xs">
									{playing ? "⏸" : "▶"}
								</span>
								<span>
									{audioError
										? "Probeer opnieuw"
										: autoplayBlocked
											? "Tik om de stem te horen"
											: "Hoor de feedback"}
								</span>
							</button>
							{audioError ? (
								<span role="alert" className="text-xs text-[var(--warn)]">
									{audioError}
								</span>
							) : null}
							<audio
								ref={ref}
								src={resolveMediaUrl(audioUrl)}
								playsInline
								preload="auto"
								onError={() => {
									console.warn(
										`[feedback-audio] element error for src=${audioUrl}`,
									);
									setAudioError("Audio kon niet geladen worden.");
								}}
								onEnded={() => {
									setPlaying(false);
									onAudioEnded?.();
								}}
								onPause={() => setPlaying(false)}
								onPlay={() => setPlaying(true)}
								className="sr-only"
								aria-hidden
							/>
						</>
					) : null}
					{studentAudioUrl ? (
						<RecordingPlayer
							src={studentAudioUrl}
							pausedLabel="Hoor uw opname"
							playingLabel="Pauzeer"
						/>
					) : null}
				</div>
			) : (
				<p className="text-xs text-[var(--ink-faint)] italic">
					Geen audio beschikbaar voor deze beurt — lees de feedback hieronder.
				</p>
			)}

			<div className="text-3xl font-mono-badge text-[var(--ink)] tabular-nums">
				{total}/{max}
			</div>

			{score.uitNotes ? (
				<p className="text-xs text-[var(--ink-faint)] italic">
					uitspraak: {score.uitNotes}
				</p>
			) : null}

			{transcript && transcript.trim().length > 0 ? (
				<div className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--panel)] px-4 py-3">
					<p className="font-mono-badge text-[10px] uppercase tracking-wider text-[var(--ink-faint)] mb-1.5">
						Wat u zei
					</p>
					<p className="text-sm leading-relaxed text-[var(--ink)]">
						{transcript}
					</p>
				</div>
			) : null}

			<details className="group border-t border-[var(--border)] pt-3">
				<summary className={DETAILS_SUMMARY_CLASSES}>
					<span
						className="inline-block transition-transform group-open:rotate-90"
						aria-hidden
					>
						▸
					</span>
					<span>Toon scoring</span>
				</summary>
				<div className="mt-3 space-y-2 text-xs">
					{CRITERIA.map((c) => (
						<CriterionBar
							key={c.key}
							c={c}
							value={scoreFor(score, c.key)}
							label={CRITERION_DUTCH_LABEL[c.key]}
						/>
					))}
				</div>
			</details>

			{english ? (
				<details className="group border-t border-[var(--border)] pt-3">
					<summary className={DETAILS_SUMMARY_CLASSES}>
						<span
							className="inline-block transition-transform group-open:rotate-90"
							aria-hidden
						>
							▸
						</span>
						<span>Toon Engelse uitleg</span>
					</summary>
					<div className="mt-3 text-sm whitespace-pre-line text-[var(--ink-muted)]">
						{english}
					</div>
				</details>
			) : null}
		</article>
	);
}
